// frontend/src/telephony/activeCallRecording/GlobalRecordingIndicator.tsx
//
// Small, status-only pill for the active-call header. Tells the user
// at a glance that recording is happening WITHOUT scrolling to the
// Recording card. No actions live here — the card owns Start/Stop.
//
// Visible when the backend effective-state reports:
//   • status === 'recording'
// Hidden otherwise (idle, stopped, starting/stopping, compliance
// never, or before the call is connected).
//
// For compliance-forced recording ('automatic' + locked), we also
// show a small 🔒 badge so the user knows they can't stop it from
// the header.

import { useGetRecordingEffectiveStateQuery } from '../../api/telephonyApi';

interface Props {
  callSid: string | null;
  /** Provider call state — indicator only renders when the call is connected. */
  activeCallKind: string;
}

export function GlobalRecordingIndicator({
  callSid,
  activeCallKind,
}: Props) {
  const isConnected =
    activeCallKind === 'connecting' || activeCallKind === 'in_call';
  const { data } = useGetRecordingEffectiveStateQuery(
    callSid ? { callSid } : (undefined as never),
    { skip: !callSid || !isConnected, pollingInterval: 3000 },
  );
  if (!callSid || !isConnected || !data) return null;
  if (data.status !== 'recording') return null;

  return (
    <span
      data-testid="global-recording-indicator"
      role="status"
      aria-label={
        data.locked
          ? 'Recording (required by workspace policy)'
          : 'Recording'
      }
      className="inline-flex items-center gap-1.5 rounded-full bg-red-50 border border-red-200 px-2 py-0.5 text-[11px] font-semibold text-red-700"
    >
      <span
        aria-hidden
        className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"
      />
      <span>Recording</span>
      {data.locked && (
        <span aria-hidden className="text-red-500 leading-none">
          🔒
        </span>
      )}
    </span>
  );
}

export default GlobalRecordingIndicator;
